import React from 'react';
import { IoClose } from 'react-icons/io5';
import {
  getBadgeAwardImagePath,
  getBadgeInfoFromId,
  TIER_COLORS,
} from '../utils/badgeAssets';
import BadgeImage from './BadgeImage';
import './GamificationCompletionPopup.css';

function toTitleCase(value) {
  if (!value || typeof value !== 'string') return '';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getBadgeName(badge) {
  if (!badge) return '';
  if (badge.name) return badge.name;
  const id = badge.id || '';
  return toTitleCase(id.replace(/_\d+$/g, ''));
}

// Individual badge names are retired for the unlocked-badge ribbon: display
// the generic category + tier (e.g. "Gas Stations Gold"). Falls back to the
// legacy name for unknown ids.
function getBadgeDisplayName(badge, info) {
  if (info?.category && info?.tier) {
    const tier = info.tier[0].toUpperCase() + info.tier.slice(1);
    return `${info.category.label} ${tier}`;
  }
  return getBadgeName(badge);
}

// The individual badge name for a tier, e.g. coffee_shop.gold ->
// "latte_legend" -> "Latte Legend". Used when naming the badge you are
// working toward, where the tier is already implied by the progress bar
// (avoids "Coffee Shops Silver (silver)").
function getTierBadgeName(info) {
  const tierName = info?.category?.tiers?.[info?.tier];
  if (tierName) return toTitleCase(tierName);
  return getBadgeDisplayName(null, info);
}

function GamificationCompletionPopup({ payload, onClose }) {
  if (!payload) return null;

  const tpAwarded = Number(payload.tpAwarded || 0) + Number(payload.bonusTPAwarded || 0);
  // Only show the per-line breakdown when more than one thing contributed TP;
  // a single source is already explained by the event title (e.g. "Restroom added").
  const tpBreakdown = Array.isArray(payload.tpBreakdown) && payload.tpBreakdown.length > 1
    ? payload.tpBreakdown
    : null;
  const badgesUnlocked = Array.isArray(payload.badgesUnlocked) ? payload.badgesUnlocked : [];
  const unlockedBadgesInfo = badgesUnlocked.map((badge) => ({
    badge,
    info: getBadgeInfoFromId(typeof badge === 'string' ? badge : badge?.id)
  }));
  const badgeProgress = payload.badgeProgress || null;
  const progressTrack = badgeProgress?.track || '';
  const progressPercent = badgeProgress?.current != null && badgeProgress?.next
    ? Math.min(100, Math.max(0, (Number(badgeProgress.current) / Number(badgeProgress.next)) * 100))
    : null;
  const targetBadgeInfo = badgeProgress?.next != null
    ? getBadgeInfoFromId(`${progressTrack}_${badgeProgress.next}`)
    : null;
  const targetBadgeName = targetBadgeInfo
    ? getTierBadgeName(targetBadgeInfo)
    : null;
  const progressRemaining =
    badgeProgress?.current != null && badgeProgress?.next != null
      ? Math.max(0, Number(badgeProgress.next) - Number(badgeProgress.current))
      : null;

  return (
    <div className="gamification-completion-popup" role="dialog" aria-live="polite">
      <div className="gamification-completion-content">
        <button
          type="button"
          className="gamification-completion-close"
          onClick={onClose}
          aria-label="Close"
        >
          <IoClose size={18} />
        </button>
        <div className="gamification-completion-title">✔ {payload.eventType === 'add_restroom' ? 'Restroom Added!' : 'Review Added!'}</div>
        <div className="gamification-completion-tp">+{tpAwarded} TP</div>

        {tpBreakdown ? (
          <div className="gamification-completion-tp-breakdown">
            {tpBreakdown.map((item, index) => (
              <div key={index} className="gamification-completion-tp-row">
                <span className="gamification-completion-tp-label">{item.label}</span>
                <span className="gamification-completion-tp-value">+{Number(item.points || 0)} TP</span>
              </div>
            ))}
          </div>
        ) : null}

        {payload.title?.changed && payload.title?.current ? (
          <div className="gamification-title-unlock" role="status" aria-label={`Level up to ${payload.title.current}`}>
            <div className="gamification-title-levelup">
              <span aria-hidden="true">✨</span> Level Up! <span aria-hidden="true">✨</span>
            </div>
            <div className="gamification-title-ladder">
              {payload.title.previous ? (
                <div className="gamification-title-from">
                  <span className="gamification-title-label">From</span>
                  <span className="gamification-title-name">{payload.title.previous}</span>
                </div>
              ) : null}
              <div className="gamification-title-arrow" aria-hidden="true">➜</div>
              <div className="gamification-title-to">
                <span className="gamification-title-label">To</span>
                <span className="gamification-title-name gamification-title-new">
                  {payload.title.current}
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {badgesUnlocked.length > 0 ? (
          <>
            <div className="gamification-completion-badge">
              🏆 New Badge{badgesUnlocked.length > 1 ? 's' : ''} Unlocked!
            </div>
            <div className="gamification-completion-badges">
              {unlockedBadgesInfo.map(({ badge, info }, index) => (
                <div key={index} className="gamification-completion-badge-item">
                  {info?.category ? (
                    <BadgeImage
                      className="gamification-completion-badge-img"
                      src={getBadgeAwardImagePath(info.category)}
                      alt={getBadgeDisplayName(badge, info)}
                      tintBorder={info?.tier ? TIER_COLORS[info.tier] : null}
                    />
                  ) : (
                    <div className="gamification-completion-badge-fallback">🏆</div>
                  )}
                  <div className="gamification-badge-ribbon">{getBadgeDisplayName(badge, info)}</div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {badgesUnlocked.length === 0 && badgeProgress?.current != null && badgeProgress?.next != null ? (
          <div className="gamification-progress-block">
            <div className="gamification-progress-count">
              {badgeProgress.current} / {badgeProgress.next} Review{badgeProgress.next !== 1 ? 's' : ''}
            </div>
            {progressRemaining > 0 ? (
              <div className="gamification-progress-sub">
                Only {progressRemaining} more to unlock your next badge!
              </div>
            ) : null}
            <div className="gamification-progress-track">
              <div
                className="gamification-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {targetBadgeInfo ? (
              <div className="gamification-target-badge">
                {targetBadgeInfo.imagePath ? (
                  <img
                    className="gamification-target-badge-img"
                    src={targetBadgeInfo.imagePath}
                    alt={targetBadgeName}
                  />
                ) : null}
                <span>
                  Next: <strong>{targetBadgeName}</strong>
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        {payload.eventType === 'add_restroom' ? (
          <div className="gamification-completion-message">
            Thanks for helping grow The Crapper Mapper community. Every restroom you add helps
            someone doo better!
          </div>
        ) : null}

        <button
          type="button"
          className="gamification-awesome-btn"
          onClick={onClose}
        >
          AWESOME!
        </button>
      </div>
    </div>
  );
}

export default GamificationCompletionPopup;
